import { LinkButton, P } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { ControlCard } from 'sections/onsite-campaigns/pages/CampaignEditor/components/ControlCard';
import styled, { useTheme } from 'styled-components';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import { useCallback, useState } from 'react';
import { ScheduledDatesModal } from './ScheduledDatesModal';
import { useFeatureFlags } from '../../../../../../../core/app/AppContext';
import { useSegmentSchedulingSelector } from './Workflows/DisplayWebContent/state/selectors/useSegmentSchedulingSelector';

const SchedulingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing3};
`;

type SchedulingControlProps = {
  messageId: string;
};

export const SchedulingControl = (props: SchedulingControlProps) => {
  const { flagEnabled } = useFeatureFlags();
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const scheduling = useSegmentSchedulingSelector(props.messageId);

  const [isScheduledDatesModalOpen, setIsScheduledDatesModalOpen] = useState(false);
  const openScheduledDatesModal = useCallback(() => setIsScheduledDatesModalOpen(true), []);
  const closeScheduledDatesModal = useCallback(() => setIsScheduledDatesModalOpen(false), []);

  if (!flagEnabled('ld-enable-onsite-segment-scheduling')) {
    return null;
  }

  return (
    <>
      <ControlCard>
        <SchedulingContent>
          <P variant="body1">
            {t('onsite.editor.workflow.displayWebContentMessage.segmentScheduling.description')}
            <span style={{ marginRight: theme.designToken.spacing1 }} />
            <InfoTooltip
              displayInline
              content={t('onsite.editor.workflow.displayWebContentMessage.segmentScheduling.descriptionTooltip')}
            />
          </P>
          <div style={{ display: 'flex', gap: theme.designToken.spacing2, flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: theme.designToken.spacing1, flexDirection: 'column' }}>
              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Starts: </strong>
                {scheduling?.startDate ? (
                  scheduling?.startTime ? (
                    <>
                      {scheduling.startDate.toLocaleDateString()} <strong>at</strong> {scheduling.startTime} UTC
                    </>
                  ) : (
                    scheduling.startDate.toLocaleDateString()
                  )
                ) : (
                  t('onsite.editor.workflow.scheduling.startDatePlaceholder')
                )}{' '}
              </P>

              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Ends:</strong>{' '}
                {scheduling?.endDate ? (
                  <>
                    {scheduling.endDate.toLocaleDateString()}{' '}
                    {scheduling?.endTime ? (
                      <>
                        <strong>at</strong> {scheduling.endTime} UTC
                      </>
                    ) : null}
                  </>
                ) : (
                  t('onsite.editor.workflow.displayWebContentMessage.segmentScheduling.endDatePlaceholder')
                )}
              </P>
            </div>
            <LinkButton
              leftSideIcon="pen"
              onClick={openScheduledDatesModal}
              ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.segmentScheduling.changeDatesAria')}
            >
              {t('onsite.editor.workflow.displayWebContentMessage.segmentScheduling.changeDates')}
            </LinkButton>
          </div>
        </SchedulingContent>
      </ControlCard>

      <ScheduledDatesModal id={props.messageId} open={isScheduledDatesModalOpen} onClose={closeScheduledDatesModal} />
    </>
  );
};
